class OmgProductCarousel extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('[data-carousel-viewport]');
    this.slides = Array.from(this.querySelectorAll('[data-carousel-slide]'));
    this.previousButton = this.querySelector('[data-carousel-previous]');
    this.nextButton = this.querySelector('[data-carousel-next]');
    this.toggleButton = this.querySelector('[data-carousel-toggle]');
    this.progress = this.querySelector('[data-carousel-progress]');
    this.progressFill = this.querySelector('[data-carousel-progress-fill]');
    this.status = this.querySelector('[data-carousel-status]');

    if (!this.viewport || this.slides.length < 2) return;

    this.autoplayEnabled = this.dataset.autoplay === 'true';
    this.intervalDuration = Math.max(Number(this.dataset.speed) || 6, 4) * 1000;
    this.pauseReasons = new Set();
    this.userPaused = false;
    this.currentIndex = 0;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.onPrevious = () => this.moveBy(-1);
    this.onNext = () => this.moveBy(1);
    this.onToggle = () => this.toggleAutoplay();
    this.onPointerEnter = () => this.setPauseReason('hover', true);
    this.onPointerLeave = () => this.setPauseReason('hover', false);
    this.onFocusIn = () => this.setPauseReason('focus', true);
    this.onFocusOut = () => {
      requestAnimationFrame(() => this.setPauseReason('focus', this.contains(document.activeElement)));
    };
    this.onVisibilityChange = () => this.setPauseReason('hidden', document.hidden);
    this.onMotionChange = () => this.setPauseReason('motion', this.reducedMotion.matches);
    this.onScroll = () => {
      cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = requestAnimationFrame(() => this.updatePosition());
    };

    this.previousButton?.addEventListener('click', this.onPrevious);
    this.nextButton?.addEventListener('click', this.onNext);
    this.toggleButton?.addEventListener('click', this.onToggle);
    this.addEventListener('pointerenter', this.onPointerEnter);
    this.addEventListener('pointerleave', this.onPointerLeave);
    this.addEventListener('focusin', this.onFocusIn);
    this.addEventListener('focusout', this.onFocusOut);
    this.viewport.addEventListener('scroll', this.onScroll, { passive: true });
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    this.reducedMotion.addEventListener('change', this.onMotionChange);

    this.resizeObserver = new ResizeObserver(() => this.updatePosition());
    this.resizeObserver.observe(this.viewport);
    this.intersectionObserver = new IntersectionObserver(
      ([entry]) => this.setPauseReason('offscreen', !entry.isIntersecting),
      { threshold: 0.25 }
    );
    this.intersectionObserver.observe(this);

    this.setPauseReason('motion', this.reducedMotion.matches);
    this.setPauseReason('hidden', document.hidden);
    this.updatePosition();
    this.syncAutoplay();
  }

  disconnectedCallback() {
    clearInterval(this.autoplayTimer);
    cancelAnimationFrame(this.scrollFrame);
    this.previousButton?.removeEventListener('click', this.onPrevious);
    this.nextButton?.removeEventListener('click', this.onNext);
    this.toggleButton?.removeEventListener('click', this.onToggle);
    this.removeEventListener('pointerenter', this.onPointerEnter);
    this.removeEventListener('pointerleave', this.onPointerLeave);
    this.removeEventListener('focusin', this.onFocusIn);
    this.removeEventListener('focusout', this.onFocusOut);
    this.viewport?.removeEventListener('scroll', this.onScroll);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.reducedMotion?.removeEventListener('change', this.onMotionChange);
    this.resizeObserver?.disconnect();
    this.intersectionObserver?.disconnect();
  }

  setPauseReason(reason, active) {
    active ? this.pauseReasons.add(reason) : this.pauseReasons.delete(reason);
    this.syncAutoplay();
  }

  syncAutoplay() {
    clearInterval(this.autoplayTimer);
    const shouldPlay = this.autoplayEnabled && !this.userPaused && this.pauseReasons.size === 0;
    this.viewport?.setAttribute('aria-live', shouldPlay ? 'off' : 'polite');
    this.classList.toggle('is-autoplaying', shouldPlay);

    if (shouldPlay) {
      this.autoplayTimer = setInterval(() => this.moveBy(1), this.intervalDuration);
    }
  }

  toggleAutoplay() {
    this.userPaused = !this.userPaused;
    this.toggleButton.setAttribute('aria-pressed', String(this.userPaused));
    this.toggleButton.setAttribute('aria-label', this.userPaused ? 'Play carousel' : 'Pause carousel');
    this.classList.toggle('is-user-paused', this.userPaused);
    this.status.textContent = this.userPaused ? 'Carousel paused' : 'Carousel playing';
    this.syncAutoplay();
  }

  moveBy(direction) {
    const targetIndex = (this.currentIndex + direction + this.slides.length) % this.slides.length;
    this.goTo(targetIndex);
  }

  goTo(index) {
    const target = this.slides[index];
    if (!target) return;

    this.viewport.scrollTo({
      left: target.offsetLeft - this.viewport.offsetLeft,
      behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
    });
    this.currentIndex = index;
    this.updateProgress();
  }

  updatePosition() {
    const viewportLeft = this.viewport.scrollLeft;
    this.currentIndex = this.slides.reduce((closestIndex, slide, index) => {
      const closestDistance = Math.abs(this.slides[closestIndex].offsetLeft - this.viewport.offsetLeft - viewportLeft);
      const distance = Math.abs(slide.offsetLeft - this.viewport.offsetLeft - viewportLeft);
      return distance < closestDistance ? index : closestIndex;
    }, 0);
    this.updateProgress();
  }

  updateProgress() {
    const position = this.currentIndex + 1;
    const ratio = position / this.slides.length;
    this.progress?.setAttribute('aria-valuenow', String(position));
    this.progressFill?.style.setProperty('--omg-carousel-progress', String(ratio));
    this.status.textContent = `Product ${position} of ${this.slides.length}`;
  }
}

if (!customElements.get('omg-product-carousel')) {
  customElements.define('omg-product-carousel', OmgProductCarousel);
}
